import apiClient from './client';

export interface ReviewRecordFlag {
  id: string;
  flag_definition_id: string;
  fact_summary: string;
  source_citation: string;
  points: number;
  severity: string;
  override_status?: string | null;
  override_reason?: string | null;
  overridden_at?: string | null;
}

export interface ReviewRecordRun {
  id: string;
  run_number: number;
  status: string;
  score: { aggregate_score: number; tier: string; has_auto_escalate: boolean } | null;
  flags: ReviewRecordFlag[];
  summary: { summary_text: string; model_used: string | null; generated_at: string | null } | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface ReviewRecord {
  case: Record<string, unknown>;
  facts: Array<Record<string, unknown>>;
  documents: Array<Record<string, unknown>>;
  public_data: Array<Record<string, unknown>>;
  review_runs: ReviewRecordRun[];
  determination: Record<string, unknown> | null;
  workflow_steps: Array<Record<string, unknown>>;
  checklist_items: Array<Record<string, unknown>>;
  notes: Array<Record<string, unknown>>;
  audit_trail: Array<Record<string, unknown>>;
  generated_at: string;
}

export async function getReviewRecord(caseId: string): Promise<ReviewRecord> {
  const { data } = await apiClient.get<ReviewRecord>(`/cases/${caseId}/review-record`);
  return data;
}

/** Fetch the print-optimized HTML and open it in a new tab for print-to-PDF. */
export async function openReviewRecordHtml(caseId: string): Promise<void> {
  const resp = await apiClient.get<string>(`/cases/${caseId}/review-record.html`, {
    responseType: 'text',
    transformResponse: (d) => d, // keep raw HTML
  });
  const blob = new Blob([resp.data as unknown as string], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    window.location.href = url;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Trigger a browser download of the review record as JSON. */
export async function downloadReviewRecordJson(caseId: string): Promise<void> {
  const record = await getReviewRecord(caseId);
  const blob = new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `review-record-${record.case.case_number || caseId}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/** Fetch the Flag Memorandum (.docx) for a completed review run and trigger
 * a browser download. Returns the Blob (used for loading state). */
export async function downloadFlagMemorandumDocx(
  runId: string,
  caseNumber?: string,
  deterministic?: boolean,
): Promise<Blob> {
  const resp = await apiClient.get(`/reviews/${runId}/flag-memorandum.docx`, {
    responseType: 'blob',
    params: deterministic ? { deterministic: true } : undefined,
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DR-${(caseNumber || runId).replace(/^DR-/, '')}-flag-memorandum.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}

/** Fetch the Flag Memorandum (.md) produced by Claude via the Anthropic
 * Messages API (Path C) for a completed review run and trigger a browser
 * download. PII is tagged + reconstructed locally; no PII leaves the box.
 * Returns the Blob (used for loading state). */
export async function downloadFlagMemorandumClaudeApiMd(
  runId: string,
  caseNumber?: string,
): Promise<Blob> {
  const resp = await apiClient.get(`/reviews/${runId}/flag-memorandum-claude-api.md`, {
    responseType: 'blob',
    timeout: 300000, // 5 min -- Claude API formatting call
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DR-${(caseNumber || runId).replace(/^DR-/, '')}-flag-memorandum-claude-api.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}

/** Flag Memorandum produced by the Claude agent + the official docx skill (follows
 * the uploaded template format). ~3min (Claude agent call). Requires the
 * isolated test container running the producer. */
export async function downloadFlagMemorandumSkillDocx(
  runId: string,
  caseNumber?: string,
  consentToken?: string,
): Promise<Blob> {
  const resp = await apiClient.get(`/reviews/${runId}/flag-memorandum-skill.docx`, {
    params: consentToken ? { consent_token: consentToken } : undefined,
    responseType: 'blob',
    timeout: 600000, // 10 min -- Claude agent call
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DR-${(caseNumber || runId).replace(/^DR-/, '')}-flag-memorandum-skill.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}

/** Flag Memorandum .pdf produced by the Claude agent + the official pdf skill. */
export async function downloadFlagMemorandumSkillPdf(
  runId: string,
  caseNumber?: string,
  consentToken?: string,
): Promise<Blob> {
  const resp = await apiClient.get(`/reviews/${runId}/flag-memorandum-skill.pdf`, {
    params: consentToken ? { consent_token: consentToken } : undefined,
    responseType: 'blob',
    timeout: 600000,
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DR-${(caseNumber || runId).replace(/^DR-/, '')}-flag-memorandum-skill.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}

/** Path B consent summary returned by the consent endpoint. */
export interface FlagMemorandumSkillConsentSummary {
  case_number?: string | null;
  issuer_name?: string | null;
  depositor_name?: string | null;
  flags_count: number;
  facts_count: number;
  format: string;
  prompt_bytes_estimate: number;
  destination: string;
  note: string;
}

/** Obtain a single-use admin consent token for a Path B export (Fix 2).
 * The token MUST be passed to downloadFlagMemorandumSkillDocx/Pdf as
 * consentToken. 5-minute TTL, single-use. */
export async function requestFlagMemorandumSkillConsent(
  runId: string,
  format: 'docx' | 'pdf',
): Promise<{ consent_token: string; expires_in_s: number; single_use: boolean; summary: FlagMemorandumSkillConsentSummary }> {
  const { data } = await apiClient.post(
    `/reviews/${runId}/flag-memorandum-skill/consent`,
    { format, acknowledged: true },
  );
  return data as { consent_token: string; expires_in_s: number; single_use: boolean; summary: FlagMemorandumSkillConsentSummary };
}

/** Fetch the review record as a nicely-formatted Word (.docx) document and
 * trigger a browser download. Returns the Blob (used for loading state). */
export async function downloadReviewRecordDocx(caseId: string, caseNumber?: string): Promise<Blob> {
  const resp = await apiClient.get(`/cases/${caseId}/review-record.docx`, {
    responseType: 'blob',
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DR-${(caseNumber || caseId).replace(/^DR-/, '')}-summary.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}

// ===========================================================================
// DIR-evidence-ledger: per-line provenance chain + source-explanation export.
// ===========================================================================

export interface EvidenceLedgerLlmCall {
  model?: string | null;
  path?: string | null;
  egress_log_id?: string | null;
  response_excerpt?: string | null;
  note?: string;
  egress_log_row?: {
    id: string;
    category: string;
    source_module: string;
    request_payload?: unknown;
    response_meta?: unknown;
    ts?: string | null;
  };
}

export interface EvidenceLedgerUnverifiedClaim {
  clause: string;
  citation: string;
  cited_fact_id: string;
  claim_value_found: string;
  db_value: string;
  mismatch_reason: string;
  status: 'unverified';
}

export interface EvidenceLedgerPoint {
  point_id: string;
  output_ref: string;
  output_text: string;
  kind: 'flag_narrative' | 'case_fact' | 'public_data' | 'llm_synthesis' | 'deterministic';
  evidence: {
    case_facts?: Array<{ field: string; value: string | null; source_type: string; provenance?: { source_doc?: string | null; source_doc_id?: string | null } }>;
    flag_trigger?: Record<string, unknown>;
    public_data_result?: Record<string, unknown>;
    llm_call?: EvidenceLedgerLlmCall;
  };
  unverified_claims?: EvidenceLedgerUnverifiedClaim[];
  code_path?: string;
}

export interface EvidenceLedgerSection {
  generator: string;
  path?: string;
  model_used?: string | null;
  points: EvidenceLedgerPoint[];
}

export interface EvidenceLedger {
  run_id: string;
  case_id?: string;
  generated_at: string;
  flag_memorandum_ledger?: EvidenceLedgerSection;
  summary_ledger?: EvidenceLedgerSection;
}

/** Fetch the per-line evidence ledger for a review run. Pass includeEgress
 * to inline the full egress_log row (prompt + response metadata) for each
 * LLM call so the panel is self-contained. Throws on 404 (legacy runs). */
export async function getEvidenceLedger(
  runId: string,
  includeEgress = true,
): Promise<EvidenceLedger> {
  const { data } = await apiClient.get<EvidenceLedger>(
    `/reviews/${runId}/evidence-ledger`,
    { params: { include_egress: includeEgress } },
  );
  return data;
}

/** Download the per-line source-explanation companion document (.docx|json)
 * for the Flag Memorandum. Admin-only (UI gates the button). */
export async function downloadFlagMemorandumSources(
  runId: string,
  fmt: 'docx' | 'json',
  caseNumber?: string,
): Promise<Blob> {
  const resp = await apiClient.get(
    `/reviews/${runId}/flag-memorandum-sources.${fmt}`,
    { responseType: 'blob' },
  );
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DR-${(caseNumber || runId).replace(/^DR-/, '')}-flag-memorandum-sources.${fmt}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}
